import { type ReactNode, useCallback, useRef } from 'react'
import { usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  Mic,
  Square,
  XCircle,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import WhatsAppTemplateSelector from '@/components/WhatsAppTemplateSelector'
import { calculateWindow } from '@/components/WhatsAppWindowIndicator'
import QuotedMessage from '@/components/QuotedMessage'
import MessageComposer from './MessageComposer'
import { useInboxContext } from './InboxContext'
import { useAudioRecording } from '@/hooks/inbox/useAudioRecording'
import { formatRecordingTime, fetchJson, validateWhatsAppFileSize } from './utils'

export default function MessageInputArea() {
  const {
    conversation,
    whatsappTemplates,
    onSendMessage,
    bodyRef,
    draftPlainTextRef,
    pendingAttachments,
    setPendingAttachments,
    chatEditorRef,
    replyingToMessage,
    setReplyingToMessage,
    conversationSource,
    macros,
    currentAgent,
    notifyTyping,
    saveDraft,
  } = useInboxContext()
  const { organizationName } = usePage<{ organizationName: string | null }>().props

  // Audio recording
  const autoSendAudioRef = useRef(false)
  const { isRecording, recordingDuration, startRecording, stopRecording } = useAudioRecording({
    setPendingAttachments,
    autoSendAudioRef,
  })

  // Typing indicator - throttled
  const typingTimerRef = useRef<number | null>(null)
  const lastTypingSentRef = useRef<number>(0)

  const handleInputChange = useCallback((value: string) => {
    bodyRef.current = value
    saveDraft(value, draftPlainTextRef.current)

    const now = Date.now()
    if (now - lastTypingSentRef.current > 2500) {
      lastTypingSentRef.current = now
      notifyTyping(true)
    }

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => {
      lastTypingSentRef.current = 0
      notifyTyping(false)
    }, 3000)
  }, [bodyRef, draftPlainTextRef, notifyTyping, saveDraft])

  const handleDraftTextChange = useCallback((text: string) => {
    draftPlainTextRef.current = text
  }, [draftPlainTextRef])

  // WhatsApp outside-window template mode
  if (
    conversation.source === 'whatsapp' &&
    !calculateWindow(conversation.whatsapp_last_inbound_at).isWithinWindow
  ) {
    return (
      <WhatsAppTemplateSelector
        templates={whatsappTemplates}
        showWindowExpiredNotice
        onSend={async (templateId, variables) => {
          try {
            const res = await fetchJson(`/conversations/${conversation.id}/messages`, {
              method: 'POST',
              body: {
                whatsapp_template_id: templateId,
                whatsapp_template_variables: variables,
              },
            })
            if (res.ok) {
              toast.success('Template enviado com sucesso')
              return true
            }
            toast.error('Erro ao enviar template')
            return false
          } catch {
            toast.error('Erro ao enviar template')
            return false
          }
        }}
      />
    )
  }

  // Audio recording buttons for WhatsApp conversations
  function renderAudioActions(): ReactNode {
    if (conversation.source !== 'whatsapp') return null

    if (isRecording) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={stopRecording}
              className="px-3 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center gap-1.5"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
              <span className="text-xs font-medium tabular-nums">{formatRecordingTime(recordingDuration)}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Parar gravação</TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={startRecording}
            className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <Mic className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Gravar áudio</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      {replyingToMessage && (
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-t border-gray-200">
          <div className="flex-1 min-w-0">
            <QuotedMessage repliedTo={{
              id: replyingToMessage.id,
              body: replyingToMessage.body,
              author_type: replyingToMessage.author_type,
              author_name: replyingToMessage.author_type === 'Agent'
                ? (replyingToMessage.author as { name: string }).name
                : (replyingToMessage.author as { name: string }).name || 'Visitante',
              attachments_count: replyingToMessage.attachments?.length || 0,
              deleted: !!replyingToMessage.deleted_at,
              image_url: replyingToMessage.attachments?.find(a => a.content_type?.startsWith('image/'))?.url,
            }} compact />
          </div>
          <button
            type="button"
            onClick={() => setReplyingToMessage(null)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}
      <MessageComposer
        onSubmit={onSendMessage}
        onChange={handleInputChange}
        onTextChange={handleDraftTextChange}
        channel={(conversation.source as 'chat' | 'email' | 'whatsapp') || 'chat'}
        macros={macros}
        endUser={conversation.end_user}
        currentAgent={currentAgent}
        organizationName={organizationName}
        pendingAttachments={pendingAttachments}
        setPendingAttachments={setPendingAttachments}
        validateFileSize={conversationSource === 'whatsapp' ? validateWhatsAppFileSize : undefined}
        editorRef={chatEditorRef}
        extraActions={renderAudioActions()}
      />
    </>
  )
}
